import FailSnackbar from '../components/ui/snackbar/fail.component'
import {
  newInitQuotationForm,
  type additionalQuotationObject,
  type dpsQuotationObject,
  type newKidsQuotationObject,
  type newLowsQuotationObject,
  type newNormalQuotationObject,
  type newQuotationForm,
  type usimQuotationObject,
} from '../lib/apis/dtos/request/quotation.request'
import type {
  AdditionalServiceItem,
  DpItem,
  KidItem,
  LowItem,
  NormalItem,
  RecentQuotationItem,
  UsimItem,
} from '../lib/apis/dtos/response/quotation.response'
import { isBlank } from './vailidate.utils'
import {
  getUsimErrorField,
  isUsimRowSubmittable,
  normalizeUsimRows,
  readUsimOpenType,
} from '../lib/apis/dtos/usim-open-type.dto'
import {
  BUNDLE_SALE_API,
  fromBundleSaleResponse,
} from '../lib/apis/dtos/bundle-sale.dto'

// 구형(RecentQuotationItem) 응답 판별: skNormalFee(구 필드)가 있고 신형 필드(skNormalFedFee)가 없을 때만.
// 제휴 시세표처럼 "아직 시세표가 없어" 빈 객체가 오는 경우는 신형으로 취급해 빈 폼을 만든다.
const isRecentItem = (data: any): data is RecentQuotationItem => {
  return (
    !!data &&
    data.skNormalFedFee === undefined &&
    data.skNormalFee !== undefined &&
    Array.isArray(data.normals)
  )
}

export const transformToNewForm = (
  data: RecentQuotationItem | newQuotationForm,
): newQuotationForm => {
  if (isRecentItem(data)) {
    return {
      ...newInitQuotationForm,
      ...data,
      skNormalPhdFee: data.skNormalFee,
      skNormalPhdPeriodType: data.skNormalIsMonth ? 'MONTH' : 'M_PLUS_N',
      skNormalPhdUsePeriod: data.skNormalPeriod,
      ktNormalPhdFee: data.ktNormalFee,
      ktNormalPhdPeriodType: data.ktNormalIsMonth ? 'MONTH' : 'M_PLUS_N',
      ktNormalPhdUsePeriod: data.ktNormalPeriod,
      lgNormalPhdFee: data.lgNormalFee,
      lgNormalPhdPeriodType: data.lgNormalIsMonth ? 'MONTH' : 'M_PLUS_N',
      lgNormalPhdUsePeriod: data.lgNormalPeriod,
      skNormalFedFee: null,
      skNormalFedPeriodType: 'MONTH',
      skNormalFedUsePeriod: null,
      ktNormalFedFee: null,
      ktNormalFedPeriodType: 'MONTH',
      ktNormalFedUsePeriod: null,
      lgNormalFedFee: null,
      lgNormalFedPeriodType: 'MONTH',
      lgNormalFedUsePeriod: null,
      normals: transNormalstoNewForm(data.normals ?? []),
      lows: transLowstoNewForm(data.lows ?? []),
      kids: transKidstoNewForm(data.kids ?? []),
      // 유심이동: 통신사마다 [이동, 신규] 행 묶음으로 맞춘다 (응답에 없는 행은 빈 행)
      usims: normalizeUsimRows(transUsimtoNewForm(data.usims ?? [])),
      dps: transDpstoNewForm(data.dps ?? []),
      additionalServices: transAdditionaltoNewForm(data.additionalServices ?? []),
      bundleSales: fromBundleSaleResponse(
        (data as unknown as Record<string, unknown>)[BUNDLE_SALE_API.formKey],
      ),
    }
  } else {
    return {
      ...newInitQuotationForm,
      ...data,
      skNormalPhdPeriodType: data?.skNormalPhdPeriodType || 'MONTH',
      ktNormalPhdPeriodType: data?.ktNormalPhdPeriodType || 'MONTH',
      lgNormalPhdPeriodType: data?.lgNormalPhdPeriodType || 'MONTH',
      skNormalFedPeriodType: data?.skNormalFedPeriodType || 'MONTH',
      ktNormalFedPeriodType: data?.ktNormalFedPeriodType || 'MONTH',
      lgNormalFedPeriodType: data?.lgNormalFedPeriodType || 'MONTH',
      // 응답에서 배열 필드가 null/누락(제휴 시세표 미제출 등)이어도 화면이 .map 으로 깨지지 않게
      normals: data?.normals ?? [],
      lows: data?.lows ?? [],
      kids: data?.kids ?? [],
      dps: data?.dps ?? [],
      usims: normalizeUsimRows(data?.usims ?? []),
      additionalServices: data?.additionalServices ?? [],
      bundleSales: fromBundleSaleResponse(
        (data as unknown as Record<string, unknown> | undefined)?.[
          BUNDLE_SALE_API.formKey
        ],
      ),
    }
  }
}

export const transNormalstoNewForm = (
  normals: NormalItem[],
): newNormalQuotationObject[] => {
  return normals.map((normal) => {
    return {
      ...normal,
      skNcPhdPrice: normal.skNcPrice,
      skCdPhdPrice: normal.skCdPrice,
      ktNcPhdPrice: normal.ktNcPrice,
      ktCdPhdPrice: normal.ktCdPrice,
      lgNcPhdPrice: normal.lgNcPrice,
      lgCdPhdPrice: normal.lgCdPrice,
      skNcFedPrice: null,
      skCdFedPrice: null,
      ktNcFedPrice: null,
      ktCdFedPrice: null,
      lgNcFedPrice: null,
      lgCdFedPrice: null,
    }
  })
}

export const transLowstoNewForm = (
  lows: LowItem[],
): newLowsQuotationObject[] => {
  return lows.map((low) => {
    return {
      ...low,
      phdFee: low.fee,
      phdPeriodType: low.isMonth ? 'MONTH' : 'DAY',
      phdUsePeriod: low.period,
      fedFee: null,
      fedUsePeriod: null,
      fedPeriodType: 'M_PLUS_N',
      ncPhdPrice: low.ncPrice,
      cdPhdPrice: low.cdPrice,
      ncFedPrice: null,
      cdFedPrice: null,
    }
  })
}

export const transKidstoNewForm = (
  kids: KidItem[],
): newKidsQuotationObject[] => {
  return kids.map((kid) => {
    return {
      ...kid,
      phdFee: kid.fee,
      phdPeriodType: kid.isMonth ? 'MONTH' : 'DAY',
      phdUsePeriod: kid.period,
      fedFee: null,
      fedUsePeriod: null,
      fedPeriodType: 'M_PLUS_N',
      ncPhdPrice: kid.ncPrice,
      cdPhdPrice: kid.cdPrice,
      naPhdPrice: kid.naPrice,
      ncFedPrice: null,
      cdFedPrice: null,
      naFedPrice: null,
    }
  })
}

export const transUsimtoNewForm = (
  usims: UsimItem[],
): usimQuotationObject[] => {
  return usims.map((usim) => {
    return {
      ...usim,
      periodType: usim.isMonth ? 'MONTH' : 'DAY',
    }
  })
}

export const transDpstoNewForm = (dps: DpItem[]): dpsQuotationObject[] => {
  return dps.map((dp) => {
    return {
      ...dp,
      periodType: dp.isMonth ? 'MONTH' : 'DAY',
    }
  })
}

export const transAdditionaltoNewForm = (
  additionalServices: AdditionalServiceItem[],
): additionalQuotationObject[] => {
  return additionalServices.map((additionalService) => {
    return {
      ...additionalService,
      usePeriod: additionalService.duration,
      periodType: additionalService.isMonth ? 'MONTH' : 'DAY',
    }
  })
}

const findFirstEmptyInput = (element: HTMLElement) => {
  const inputs = Array.from(
    element.querySelectorAll('input'),
  ) as HTMLInputElement[]

  return inputs.find(
    (input) => !input.disabled && (!input.value || input.value.trim() === ''),
  )
}

const highlightFieldCell = (field: HTMLElement) => {
  const td = field.closest('td')
  if (!td) return

  const origPosition = getComputedStyle(td).position
  if (origPosition === 'static') {
    td.style.position = 'relative'
  }

  const origBackgroundColor = td.style.backgroundColor || ''
  td.style.backgroundColor = 'var(--Pink-5, #FFF5F0)'

  const highlightDiv = document.createElement('div')
  highlightDiv.style.position = 'absolute'
  highlightDiv.style.inset = '0'
  highlightDiv.style.pointerEvents = 'none'
  highlightDiv.style.border = '1px solid var(--Red-50, #F24548)'
  highlightDiv.style.zIndex = '10'

  td.appendChild(highlightDiv)

  const hasAcceptedValue = () => {
    if (field instanceof HTMLInputElement) {
      const normalizedValue = field.value.replace(/,/g, '').trim()

      if (
        normalizedValue === '' ||
        normalizedValue === '-' ||
        normalizedValue === '.' ||
        normalizedValue === '-.'
      ) {
        return false
      }

      return /^-?\d+(\.\d+)?$/.test(normalizedValue)
    }

    return !!field.dataset.errorValue?.trim()
  }

  let observer: MutationObserver | null = null

  const cleanupHighlight = () => {
    if (td.contains(highlightDiv)) {
      td.removeChild(highlightDiv)
    }

    td.style.backgroundColor = origBackgroundColor
    if (origPosition === 'static') {
      td.style.position = ''
    }

    field.removeEventListener('input', handleInput)
    field.removeEventListener('change', handleInput)
    observer?.disconnect()
  }

  const handleInput = () => {
    requestAnimationFrame(() => {
      if (hasAcceptedValue()) {
        cleanupHighlight()
      }
    })
  }

  field.addEventListener('input', handleInput)
  field.addEventListener('change', handleInput)

  if (!(field instanceof HTMLInputElement)) {
    observer = new MutationObserver(() => {
      if (hasAcceptedValue()) {
        cleanupHighlight()
      }
    })
    observer.observe(field, {
      attributes: true,
      attributeFilter: ['data-error-value'],
    })
  }
}

const isScrollable = (element: HTMLElement) => {
  const { overflowY } = getComputedStyle(element)
  return (
    ['auto', 'scroll', 'overlay'].includes(overflowY) &&
    element.scrollHeight > element.clientHeight
  )
}

const findScrollParent = (element: HTMLElement) => {
  let parent = element.parentElement

  while (parent) {
    if (isScrollable(parent)) {
      return parent
    }
    parent = parent.parentElement
  }

  return null
}

const scrollElementIntoContainer = (element: HTMLElement) => {
  const scrollParent = findScrollParent(element)

  if (!scrollParent) {
    element.scrollIntoView({ behavior: 'smooth', block: 'center' })
    return
  }

  const elementRect = element.getBoundingClientRect()
  const parentRect = scrollParent.getBoundingClientRect()
  const nextTop =
    scrollParent.scrollTop +
    (elementRect.top - parentRect.top) -
    scrollParent.clientHeight / 2 +
    elementRect.height / 2

  scrollParent.scrollTo({
    top: Math.max(nextTop, 0),
    behavior: 'smooth',
  })
}

const scrollToError = (id: string, focusField?: string) => {
  const element = document.getElementById(id)
  if (!element) return

  const htmlElement = element as HTMLElement

  let targetField: HTMLElement | undefined

  if (focusField) {
    const matchedField = htmlElement.querySelector(
      `[data-error-field="${focusField}"]`,
    )

    if (matchedField instanceof HTMLElement) {
      targetField = matchedField
    }
  }

  if (!targetField) {
    targetField = findFirstEmptyInput(htmlElement)
  }

  const scrollTarget = targetField || htmlElement

  if (targetField) {
    setTimeout(() => {
      scrollElementIntoContainer(scrollTarget)
      targetField.focus({ preventScroll: true })
      highlightFieldCell(targetField)
    }, 100)
    return
  }

  const buttons = htmlElement.querySelectorAll('button')
  if (buttons.length > 0) {
    setTimeout(() => {
      scrollElementIntoContainer(htmlElement)
      buttons[0].focus()
    }, 100)
    return
  }

  htmlElement.setAttribute('tabindex', '-1')
  setTimeout(() => {
    scrollElementIntoContainer(htmlElement)
    htmlElement.focus({ preventScroll: true })
    htmlElement.removeAttribute('tabindex')
  }, 100)
}

/**
 * 숫자로 해석되지 않는 입력값인지 판별.
 * PriceInput 은 입력 중간 상태를 허용하므로 '-', '.', '-.' 처럼
 * 부호/소수점만 남은 값이 그대로 폼에 저장될 수 있다. (미입력은 false — 항목 미입력 검증에서 처리)
 */
export const isInvalidNumberInput = (val: unknown): boolean => {
  if (isBlank(val)) return false
  if (typeof val === 'number') return !Number.isFinite(val)
  if (typeof val !== 'string') return false

  const normalized = val.replace(/,/g, '').trim()
  if (normalized === '') return false

  // 입력 중 남을 수 있는 소수점 끝자리('12.')는 숫자로 인정
  return !/^-?\d+\.?\d*$/.test(normalized)
}

/** 필드명을 사용자에게 보여줄 라벨로 변환 (ex. skNcPhdPrice → SKT 번이 공시지원 가격) */
const humanizeFieldName = (field: string) => {
  if (field === 'serviceFee') return '월 요금'
  if (field === 'nonSubscriptionFee') return '미가입시 추가금'

  const telecom = /^sk/.test(field)
    ? 'SKT'
    : /^kt/.test(field)
      ? 'KT'
      : /^lg/.test(field)
        ? 'LG U+'
        : ''
  const contract = /nc/i.test(field)
    ? '번이'
    : /cd/i.test(field)
      ? '기변'
      : /na/i.test(field)
        ? '신규'
        : ''
  const benefit = /phd/i.test(field)
    ? '공시지원'
    : /fed/i.test(field)
      ? '선택약정'
      : ''
  const kind = /price$/i.test(field)
    ? '가격'
    : /fee$/i.test(field)
      ? '필수 요금'
      : /useperiod$/i.test(field)
        ? '유지 기간'
        : ''

  return [telecom, contract, benefit, kind].filter(Boolean).join(' ') || field
}

const NORMAL_FED_PHD_FIELDS = [
  'skNormalFedFee',
  'skNormalFedUsePeriod',
  'ktNormalFedFee',
  'ktNormalFedUsePeriod',
  'lgNormalFedFee',
  'lgNormalFedUsePeriod',
  'skNormalPhdFee',
  'skNormalPhdUsePeriod',
  'ktNormalPhdFee',
  'ktNormalPhdUsePeriod',
  'lgNormalPhdFee',
  'lgNormalPhdUsePeriod',
]

const NORMAL_PRICE_FIELDS = [
  'skNcPhdPrice',
  'skCdPhdPrice',
  'ktNcPhdPrice',
  'ktCdPhdPrice',
  'lgNcPhdPrice',
  'lgCdPhdPrice',
  'skNcFedPrice',
  'skCdFedPrice',
  'ktNcFedPrice',
  'ktCdFedPrice',
  'lgNcFedPrice',
  'lgCdFedPrice',
]

const LOW_PRICE_FIELDS = [
  'phdFee',
  'phdUsePeriod',
  'ncPhdPrice',
  'cdPhdPrice',
  'fedFee',
  'fedUsePeriod',
  'ncFedPrice',
  'cdFedPrice',
]

const KIDS_PRICE_FIELDS = [
  'phdFee',
  'phdUsePeriod',
  'naPhdPrice',
  'ncPhdPrice',
  'cdPhdPrice',
  'fedFee',
  'fedUsePeriod',
  'naFedPrice',
  'ncFedPrice',
  'cdFedPrice',
]

const DP_PRICE_FIELDS = ['fee', 'usePeriod', 'ncPrice', 'cdPrice']

/** 시세표 행(주요폰·별도정책·키즈·DP·부가서비스·유심) 공통 — 필드는 이름으로 읽는다 */
type NumberFieldItem = object

const readField = (item: NumberFieldItem, field: string): unknown =>
  (item as Record<string, unknown>)[field]

interface NumberFieldGroup {
  items: NumberFieldItem[] | undefined
  label: string
  sectionId: string
  rowId: (item: NumberFieldItem, index: number) => string
  fields: string[]
  /** 폼 필드명과 data-error-field 이름이 다른 경우 */
  errorFieldOf?: (field: string, item: NumberFieldItem) => string
  /** 해당 행에서 검증을 건너뛸 필드 */
  skipField?: (item: NumberFieldItem, field: string) => boolean
}

const asText = (val: unknown) => (val == null ? '' : String(val))

const readText = (item: NumberFieldItem, field: string) =>
  asText(readField(item, field))

/**
 * 제출 전 숫자 필드 검증 — '-' 처럼 숫자가 아닌 값이 남아 있으면
 * 안내 메시지 + 해당 위치로 스크롤 + 빨간 라인 표시 후 제출을 막는다.
 */
const validateNumberInputs = (quotation: newQuotationForm) => {
  const groups: NumberFieldGroup[] = [
    {
      items: [quotation],
      label: '필수 요금/ 유지 기간',
      sectionId: 'fee-section',
      rowId: () => 'fee-section',
      fields: NORMAL_FED_PHD_FIELDS,
    },
    {
      items: quotation.normals,
      label: '일괄 정책 시세',
      sectionId: 'main-section',
      rowId: (item) =>
        `${readText(item, 'deviceId')}/${readText(item, 'storage')}`,
      fields: NORMAL_PRICE_FIELDS,
    },
    {
      items: quotation.lows,
      label: '별도 정책 시세',
      sectionId: 'lows-section',
      rowId: (item) =>
        `${readText(item, 'deviceId')}/${readText(item, 'uuid')}`,
      fields: LOW_PRICE_FIELDS,
    },
    {
      items: quotation.kids,
      label: '키즈폰 시세',
      sectionId: 'kids-section',
      rowId: (item) =>
        `${readText(item, 'deviceId')}/${readText(item, 'uuid')}`,
      fields: KIDS_PRICE_FIELDS,
    },
    {
      items: quotation.dps,
      label: '디피폰 시세',
      sectionId: 'dps-section',
      rowId: (item) =>
        `${readText(item, 'deviceId')}/${readText(item, 'uuid')}`,
      fields: DP_PRICE_FIELDS,
    },
    {
      items: quotation.additionalServices,
      label: '부가서비스',
      sectionId: 'additional-section',
      rowId: (_item, index) => `additional-${index}`,
      fields: ['serviceFee', 'usePeriod', 'nonSubscriptionFee'],
      errorFieldOf: (field) =>
        field === 'serviceFee'
          ? 'additionalServiceFee'
          : field === 'usePeriod'
            ? 'additionalUsePeriod'
            : field,
      // 필수 부가서비스는 미가입시 추가금 칸이 '-' 로 표시되는 자리라 검증 제외
      skipField: (item, field) =>
        field === 'nonSubscriptionFee' && !!readField(item, 'isMandatory'),
    },
    {
      // 비어 있는 신규 행(미제공)은 제출하지 않으므로 검증에서도 제외
      items: (quotation.usims || []).filter(isUsimRowSubmittable),
      label: '유심이동',
      sectionId: 'usim-section',
      rowId: (item) => readText(item, 'telecom'),
      fields: ['fee', 'usePeriod', 'price'],
      errorFieldOf: (field, item) =>
        getUsimErrorField(field as 'fee' | 'usePeriod' | 'price', readUsimOpenType(item)),
    },
  ]

  for (const group of groups) {
    const items = group.items || []

    for (let index = 0; index < items.length; index++) {
      const item = items[index]
      const invalidField = group.fields.find(
        (field) =>
          !group.skipField?.(item, field) &&
          isInvalidNumberInput(readField(item, field)),
      )

      if (!invalidField) continue

      const deviceName = readText(item, 'deviceName')
        ? ` - ${readText(item, 'deviceName')}`
        : ''
      FailSnackbar(
        `시세표 제출 실패 (숫자만 입력해주세요: ${group.label}${deviceName} ${humanizeFieldName(invalidField)})`,
      )
      scrollToError(
        group.rowId(item, index) || group.sectionId,
        group.errorFieldOf ? group.errorFieldOf(invalidField, item) : invalidField,
      )
      return true
    }
  }

  return false
}

export const submitCondition = (quotation: newQuotationForm): boolean => {
  if (validateNumberInputs(quotation)) return false
  if (validateNormalQuotation(quotation)) return false
  if (
    validateQuotationItems(
      quotation.lows,
      '별도 정책',
      quotation.isLowFedActive as boolean,
      'lows-section',
      true,
    )
  )
    return false
  if (
    validateQuotationItems(
      quotation.kids,
      '키즈폰',
      quotation.isKidsFedActive as boolean,
      'kids-section',
      true,
    )
  )
    return false

  // 2. 부가서비스 검증
  const firstInvalidAdditionalIndex = (
    quotation?.additionalServices || []
  ).findIndex((item) => isBlank(item.serviceFee) || isBlank(item.usePeriod))
  if (firstInvalidAdditionalIndex >= 0) {
    const invalidAdditional =
      quotation.additionalServices?.[firstInvalidAdditionalIndex]
    const errorField = isBlank(invalidAdditional?.usePeriod)
      ? 'additionalUsePeriod'
      : 'additionalServiceFee'

    FailSnackbar('시세표 제출 실패 (항목 미입력: 부가서비스)')
    scrollToError(`additional-${firstInvalidAdditionalIndex}`, errorField)
    return false
  }

  // 3. 유심이동 상품 검증
  const firstInvalidUsim = (quotation?.usims || []).filter(isUsimRowSubmittable).find(
    (item) =>
      isBlank(item.fee) ||
      isBlank(item.periodType) ||
      isBlank(item.usePeriod) ||
      isBlank(item.price),
  )
  if (firstInvalidUsim) {
    const invalidUsimField = isBlank(firstInvalidUsim.fee)
      ? 'fee'
      : isBlank(firstInvalidUsim.periodType)
        ? 'periodType'
        : isBlank(firstInvalidUsim.usePeriod)
          ? 'usePeriod'
          : 'price'
    const errorField = getUsimErrorField(invalidUsimField, readUsimOpenType(firstInvalidUsim))

    FailSnackbar('시세표 제출 실패 (항목 미입력: 유심이동 상품)')
    scrollToError(firstInvalidUsim.telecom, errorField)
    return false
  }

  // 모든 검증을 통과했을 때만 true 반환
  return true
}

const getQuotationItemsValidationError = (
  items: any[] | undefined,
  isFedActive: boolean,
  requireTelecom = false,
) => {
  let telecomMissingName = ''
  let fedMissingName = ''
  let phdMissingName = ''
  let errorElementId = ''
  let errorFocusField = ''

  items?.some((item) => {
    const itemId = `${item.deviceId}/${item.uuid}`
    const hasFedPrice =
      isFedActive &&
      (!isBlank(item.naFedPrice) ||
        !isBlank(item.cdFedPrice) ||
        !isBlank(item.ncFedPrice))
    const hasPhdPrice =
      !isBlank(item.naPhdPrice) ||
      !isBlank(item.cdPhdPrice) ||
      !isBlank(item.ncPhdPrice)
    const hasAnyEnteredValue =
      hasFedPrice ||
      hasPhdPrice ||
      !isBlank(item.phdFee) ||
      !isBlank(item.phdPeriodType) ||
      !isBlank(item.phdUsePeriod) ||
      !isBlank(item.fedFee) ||
      !isBlank(item.fedPeriodType) ||
      !isBlank(item.fedUsePeriod)

    if (
      requireTelecom &&
      !telecomMissingName &&
      isBlank(item.telecom) &&
      hasAnyEnteredValue
    ) {
      telecomMissingName = item.deviceName || '미지정 기기'
      errorElementId = itemId
      errorFocusField = 'telecom'
      return true
    }

    const phdFocusField = hasPhdPrice
      ? isBlank(item.phdFee)
        ? 'phdFee'
        : isBlank(item.phdPeriodType)
          ? 'phdPeriodType'
          : isBlank(item.phdUsePeriod)
            ? 'phdUsePeriod'
            : ''
      : ''

    const fedFocusField = hasFedPrice
      ? isBlank(item.fedFee)
        ? 'fedFee'
        : isBlank(item.fedPeriodType)
          ? 'fedPeriodType'
          : isBlank(item.fedUsePeriod)
            ? 'fedUsePeriod'
            : ''
      : ''

    if (!phdMissingName && phdFocusField) {
      phdMissingName = item.deviceName || '미지정 기기'
      if (!errorElementId) {
        errorElementId = itemId
        errorFocusField = phdFocusField
      }
    }

    if (!fedMissingName && fedFocusField) {
      fedMissingName = item.deviceName || '미지정 기기'
      if (!errorElementId) {
        errorElementId = itemId
        errorFocusField = fedFocusField
      }
    }

    return (fedMissingName || !isFedActive) && !!phdMissingName
  })

  return {
    telecomMissingName,
    fedMissingName,
    phdMissingName,
    errorElementId,
    errorFocusField,
  }
}

export const focusQuotationSubmitServerError = (
  code: number | undefined,
  quotation: newQuotationForm,
) => {
  if (code !== 31111) {
    return false
  }

  const {
    telecomMissingName,
    fedMissingName,
    phdMissingName,
    errorElementId,
    errorFocusField,
  } =
    getQuotationItemsValidationError(
      quotation.kids,
      quotation.isKidsFedActive as boolean,
      true,
    )

  if (!telecomMissingName && !fedMissingName && !phdMissingName) {
    scrollToError('kids-section')
    return true
  }

  scrollToError(errorElementId || 'kids-section', errorFocusField)
  return true
}

const validateQuotationItems = (
  items: any[] | undefined,
  categoryName: string,
  isFedActive: boolean,
  sectionId: string,
  requireTelecom = false,
) => {
  const {
    telecomMissingName,
    fedMissingName,
    phdMissingName,
    errorElementId,
    errorFocusField,
  } = getQuotationItemsValidationError(items, isFedActive, requireTelecom)

  if (telecomMissingName) {
    FailSnackbar(
      `시세표 제출 실패(항목 미입력: ${categoryName} - ${telecomMissingName} 통신사)`,
    )
    scrollToError(errorElementId || sectionId, errorFocusField)
    return true
  }

  if (fedMissingName && phdMissingName) {
    FailSnackbar(
      `시세표 제출 실패(항목 미입력: ${categoryName} - ${fedMissingName || phdMissingName} 공시지원, 선택약정)`,
    )
    scrollToError(errorElementId || sectionId, errorFocusField)
    return true
  } else if (fedMissingName) {
    FailSnackbar(
      `시세표 제출 실패(항목 미입력: ${categoryName} - ${fedMissingName} 선택약정)`,
    )
    scrollToError(errorElementId || sectionId, errorFocusField)
    return true
  } else if (phdMissingName) {
    FailSnackbar(
      `시세표 제출 실패(항목 미입력: ${categoryName} - ${phdMissingName} 공시지원)`,
    )
    scrollToError(errorElementId || sectionId, errorFocusField)
    return true
  }

  return false
}

const validateNormalQuotation = (quotation: newQuotationForm) => {
  let errorMsg = ''
  let errorElementId = ''
  let errorFocusField = ''

  // normals 배열 중 하나라도 조건에 걸리면 중단
  quotation.normals?.some((item) => {
    const configs = [
      {
        name: 'SKT',
        fedPrice: [item.skNcFedPrice, item.skCdFedPrice],
        phdPrice: [item.skNcPhdPrice, item.skCdPhdPrice],
        fedFee: quotation.skNormalFedFee,
        fedPeriodType: quotation.skNormalFedPeriodType,
        fedPeriod: quotation.skNormalFedUsePeriod,
        phdFee: quotation.skNormalPhdFee,
        phdPeriodType: quotation.skNormalPhdPeriodType,
        phdPeriod: quotation.skNormalPhdUsePeriod,
        fedFeeField: 'skNormalFedFee',
        fedPeriodTypeField: 'skNormalFedPeriodType',
        fedPeriodField: 'skNormalFedUsePeriod',
        phdFeeField: 'skNormalPhdFee',
        phdPeriodTypeField: 'skNormalPhdPeriodType',
        phdPeriodField: 'skNormalPhdUsePeriod',
      },
      {
        name: 'KT',
        fedPrice: [item.ktNcFedPrice, item.ktCdFedPrice],
        phdPrice: [item.ktNcPhdPrice, item.ktCdPhdPrice],
        fedFee: quotation.ktNormalFedFee,
        fedPeriodType: quotation.ktNormalFedPeriodType,
        fedPeriod: quotation.ktNormalFedUsePeriod,
        phdFee: quotation.ktNormalPhdFee,
        phdPeriodType: quotation.ktNormalPhdPeriodType,
        phdPeriod: quotation.ktNormalPhdUsePeriod,
        fedFeeField: 'ktNormalFedFee',
        fedPeriodTypeField: 'ktNormalFedPeriodType',
        fedPeriodField: 'ktNormalFedUsePeriod',
        phdFeeField: 'ktNormalPhdFee',
        phdPeriodTypeField: 'ktNormalPhdPeriodType',
        phdPeriodField: 'ktNormalPhdUsePeriod',
      },
      {
        name: 'LG',
        fedPrice: [item.lgNcFedPrice, item.lgCdFedPrice],
        phdPrice: [item.lgNcPhdPrice, item.lgCdPhdPrice],
        fedFee: quotation.lgNormalFedFee,
        fedPeriodType: quotation.lgNormalFedPeriodType,
        fedPeriod: quotation.lgNormalFedUsePeriod,
        phdFee: quotation.lgNormalPhdFee,
        phdPeriodType: quotation.lgNormalPhdPeriodType,
        phdPeriod: quotation.lgNormalPhdUsePeriod,
        fedFeeField: 'lgNormalFedFee',
        fedPeriodTypeField: 'lgNormalFedPeriodType',
        fedPeriodField: 'lgNormalFedUsePeriod',
        phdFeeField: 'lgNormalPhdFee',
        phdPeriodTypeField: 'lgNormalPhdPeriodType',
        phdPeriodField: 'lgNormalPhdUsePeriod',
      },
    ]

    for (const conf of configs) {
      if (quotation.isMainFedActive) {
        const hasFedPrice = conf.fedPrice.some((p) => !isBlank(p))

        if (hasFedPrice) {
          const isFeeEmpty = isBlank(conf.fedFee)
          const isPeriodTypeEmpty = isBlank(conf.fedPeriodType)
          const isPeriodEmpty = isBlank(conf.fedPeriod)

          if (isFeeEmpty || isPeriodTypeEmpty || isPeriodEmpty) {
            const detail =
              isFeeEmpty && (isPeriodTypeEmpty || isPeriodEmpty)
                ? '필수 요금, 유지 기간'
                : isFeeEmpty
                  ? '필수 요금'
                  : '유지 기간'
            errorMsg = `시세표 제출 실패 (항목 미입력 : ${detail} - 선택약정)`
            errorElementId = 'fee-section' // 필수요금제 입력 테이블이 포함된 부모 ID
            errorFocusField = isFeeEmpty
              ? conf.fedFeeField
              : isPeriodTypeEmpty
                ? conf.fedPeriodTypeField
                : conf.fedPeriodField
            return true
          }
        }
      }

      const hasPhdPrice = conf.phdPrice.some((p) => !isBlank(p))
      if (hasPhdPrice) {
        const isFeeEmpty = isBlank(conf.phdFee)
        const isPeriodTypeEmpty = isBlank(conf.phdPeriodType)
        const isPeriodEmpty = isBlank(conf.phdPeriod)

        if (isFeeEmpty || isPeriodTypeEmpty || isPeriodEmpty) {
          const detail =
            isFeeEmpty && (isPeriodTypeEmpty || isPeriodEmpty)
              ? '필수 요금, 유지 기간'
              : isFeeEmpty
                ? '필수 요금'
                : '유지 기간'
          errorMsg = `시세표 제출 실패 (항목 미입력 : ${detail} - 공시지원)`
          errorElementId = 'fee-section' // 필수요금제 입력 테이블이 포함된 부모 ID
          errorFocusField = isFeeEmpty
            ? conf.phdFeeField
            : isPeriodTypeEmpty
              ? conf.phdPeriodTypeField
              : conf.phdPeriodField
          return true
        }
      }
    }
    return false
  })

  if (errorMsg) {
    FailSnackbar(errorMsg)
    scrollToError(errorElementId, errorFocusField)
    return true
  }
  return false
}
